import type { Amount, Points, PointsEpoch, RewardsConfig } from '@/api/spec.ts';
import { offsetNumberToNumber } from '@/utils/tokenNumberUtils';
import type { PointsHistoryEntry } from './types';

// The backend ships pre-season as epoch 0 (with the real campaign dates); epoch naming lives here.
const PRE_SEASON_EPOCH = 0;

// Points arrive as an Amount (string) scaled by the collateral token decimals. They're a
// display-only figure kept to 2 decimals so small traders' fractional contributions stay visible,
// floored (never rounded up). The floor runs while the value is still integer-scaled — flooring
// after the full division would lose an exact hundredth to binary float error (4.35 → 4.34).
export const pointsAmountToDisplay = (amount: Amount, decimals: number): number =>
  offsetNumberToNumber(Math.floor(offsetNumberToNumber(Number(amount), decimals - 2)), 2);

// Render helper for the above (Points and mPoints): values are pre-floored to 2 decimals, so show 2
// and strip the trailing ".00" from whole numbers. Pass to the locale-aware number formatter.
export const POINTS_DISPLAY_FORMAT = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  trailingZeroDisplay: 'stripIfInteger',
} as const;

export interface PointsCampaign {
  /** 1-based index of the in-progress epoch, 0 when the campaign hasn't started. */
  currentEpoch: number;
  /** End of the current epoch (ms); campaign start during pre-season, 0 when config is missing. */
  currentEpochEndMs: number;
  /** Start of epoch 1 / the whole campaign (ms), 0 when config is missing. */
  campaignStartMs: number;
  /** Length of one epoch (ms), 0 when config is missing. */
  epochDurationMs: number;
}

export const computePointsCampaign = (rewards: RewardsConfig | undefined, nowMs: number = Date.now()): PointsCampaign => {
  const startsAt = rewards?.points_epoch_starts_at ?? 0;
  const durationMs = (rewards?.points_epoch_duration_sec ?? 0) * 1000;

  let currentEpoch = 0;
  if (startsAt > 0 && durationMs > 0) {
    const elapsed = nowMs - startsAt;
    if (elapsed >= 0) {
      currentEpoch = Math.floor(elapsed / durationMs) + 1;
    }
  }

  // Pre-season counts down to the campaign start; startsAt is 0 when config is missing.
  const currentEpochEndMs = currentEpoch > 0 ? startsAt + currentEpoch * durationMs : startsAt;

  return { currentEpoch, currentEpochEndMs, campaignStartMs: startsAt, epochDurationMs: durationMs };
};

export const mapPointsEpochToRow = (
  epoch: PointsEpoch,
  currentEpoch: number,
  decimals: number,
  showRealPoints: boolean
): PointsHistoryEntry => {
  const isPreSeason = epoch.e === PRE_SEASON_EPOCH;
  const startSec = Math.floor(epoch.sts / 1000);
  const endSec = startSec + epoch.dur - 1;
  // Guard against currentEpoch === 0 matching the pre-season row when config is missing.
  const isCurrent = currentEpoch > 0 && epoch.e === currentEpoch;
  const points = epoch.p != null ? pointsAmountToDisplay(epoch.p, decimals) : null;

  return {
    epoch: epoch.e,
    epochName: isPreSeason ? 'Pre-Season' : `Week ${epoch.e}`,
    startDate: startSec,
    endDate: endSec,
    weeklyRank: epoch.r || null,
    // A returned weekly row always carries a number (nullish -> 0); pre-season renders "TBA" regardless.
    weeklyPoints: isPreSeason ? points : (points ?? 0),
    isCurrent,
    // The API ships real points immediately; until `showRealPoints` is on we mask every finished
    // weekly row as "pending" so values stay hidden. Pre-season and the live row never pend.
    isPending: !isPreSeason && !isCurrent && !showRealPoints,
  };
};

// Synthesize a weekly epoch row from config for a week the backend doesn't return: the in-progress
// week (live: green dates + LiveDot) and finished weeks whose points haven't been distributed yet
// (pending: rank/points as "pending…"). Dates use the same `dur - 1` convention as settled rows
// (mapPointsEpochToRow) so the period doesn't shift by a day once the epoch settles.
const buildSyntheticEpochRow = (campaign: PointsCampaign, epoch: number, isCurrent: boolean): PointsHistoryEntry | null => {
  if (epoch <= 0 || campaign.epochDurationMs <= 0 || campaign.campaignStartMs <= 0) return null;
  const startMs = campaign.campaignStartMs + (epoch - 1) * campaign.epochDurationMs;
  const startSec = Math.floor(startMs / 1000);
  const endSec = startSec + Math.floor(campaign.epochDurationMs / 1000) - 1;
  return {
    epoch,
    epochName: `Week ${epoch}`,
    startDate: startSec,
    endDate: endSec,
    weeklyRank: null,
    weeklyPoints: null,
    isCurrent,
    isPending: !isCurrent,
  };
};

// The backend only returns pre-season (e:0) and weekly epochs once their points are distributed —
// the in-progress week and any finished week still awaiting distribution are omitted entirely.
// Reconstruct the full newest -> oldest list, synthesizing every missing week (live for the current
// one, pending for finished-but-undistributed ones) so the grid shows no gaps.
export const buildPointsRows = (
  data: Points | undefined,
  campaign: PointsCampaign,
  decimals: number,
  showRealPoints: boolean
): PointsHistoryEntry[] => {
  // No payload means disconnected/unauthorized — keep the grid empty so the "Connect to see
  // Points" overlay shows instead of synthesized rows.
  if (!data) return [];

  const settledByEpoch = new Map<number, PointsHistoryEntry>();
  for (const epoch of data.d) {
    settledByEpoch.set(epoch.e, mapPointsEpochToRow(epoch, campaign.currentEpoch, decimals, showRealPoints));
  }

  // Before the campaign starts there are no weeks to synthesize — show whatever the backend returned.
  if (campaign.currentEpoch <= 0) return [...settledByEpoch.values()];

  // Current (live) week down to week 1, filling any week the backend omitted.
  const rows: PointsHistoryEntry[] = [];
  for (let epoch = campaign.currentEpoch; epoch >= 1; epoch--) {
    const settled = settledByEpoch.get(epoch);
    if (settled) {
      rows.push(settled);
      continue;
    }
    const synthetic = buildSyntheticEpochRow(campaign, epoch, epoch === campaign.currentEpoch);
    if (synthetic) rows.push(synthetic);
  }

  // Pre-season last.
  const preSeason = settledByEpoch.get(PRE_SEASON_EPOCH);
  if (preSeason) rows.push(preSeason);

  return rows;
};
